import type { DefectStatus, DefectSummary, QuestionSeverity } from '@my/check-api';
import { Camera } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  SEVERITY_BADGE,
  SEVERITY_STRIPE,
  STATUS_BADGE,
  isUnresolved,
} from './lib/defectTypes';
import { useWhenLabel } from './useWhenLabel';

interface DefectQueueProps {
  defects: DefectSummary[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  severityLabels: Record<QuestionSeverity, string>;
  statusLabels: Record<DefectStatus, string>;
}

/**
 * Left-hand queue. One card per defect: severity stripe down the left edge,
 * registration + badges on top, then the fault and who raised it. The assigned
 * garage shows as a footer line once one is set.
 *
 * Ordering is the server's — unresolved, then most severe, then oldest — so
 * the 200-row cap keeps the most urgent rather than an arbitrary slice.
 *
 * The header count is unresolved defects, not the card count: resolved ones
 * stay in the list (dimmed) so an operator sees what just happened rather than
 * watching a card vanish.
 */
export function DefectQueue({
  defects,
  selectedId,
  onSelect,
  severityLabels,
  statusLabels,
}: DefectQueueProps) {
  const { t } = useTranslation('checks');
  const whenLabel = useWhenLabel();
  const openCount = defects.filter(isUnresolved).length;

  return (
    <div className="flex h-full min-h-0 flex-col rounded-sm border border-slate-300 bg-white shadow-sm">
      <header className="flex items-center justify-between gap-2 border-b border-slate-200 px-3 py-2.5">
        <h2 className="text-base font-semibold text-slate-900">
          {t('defects.queue.title')} ({openCount})
        </h2>
      </header>

      <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-3">
        {defects.length === 0 ? (
          <p className="px-1 py-6 text-center text-xs italic text-slate-400">
            {t('defects.queue.empty')}
          </p>
        ) : (
          defects.map((defect) => {
            const isSelected = defect.id === selectedId;
            const resolved = !isUnresolved(defect);
            return (
              <button
                key={defect.id}
                type="button"
                onClick={() => onSelect(defect.id)}
                aria-current={isSelected}
                className={`relative w-full overflow-hidden rounded-sm border py-2 pl-4 pr-2.5 text-left transition-colors ${
                  isSelected
                    ? 'border-blue-300 bg-blue-50/60'
                    : 'border-slate-300 bg-white hover:bg-slate-50'
                } ${resolved ? 'opacity-60' : ''}`}
              >
                <span
                  className={`absolute inset-y-0 left-0 w-1 ${SEVERITY_STRIPE[defect.severity]}`}
                  aria-hidden="true"
                />

                <div className="flex items-start justify-between gap-2">
                  <span className="flex items-center gap-1.5">
                    <span className="rounded-sm border border-slate-200 bg-slate-50 px-1.5 py-0.5 font-mono text-[10px] font-medium text-slate-700">
                      {defect.resourceRegistration}
                    </span>
                    {/* Which defects come with evidence, before opening one. */}
                    {defect.hasPhoto && (
                      <Camera className="h-3.5 w-3.5 text-slate-400" aria-label={t('defects.hasPhoto')} />
                    )}
                  </span>
                  <span className="flex flex-shrink-0 items-center gap-1">
                    {/* Overdue outranks everything else on the card. */}
                    {defect.isOverdue && (
                      <Badge className="border-red-500 bg-red-500 text-white">
                        {t('defects.overdue')}
                      </Badge>
                    )}
                    <Badge className={SEVERITY_BADGE[defect.severity]}>
                      {severityLabels[defect.severity]}
                    </Badge>
                    <Badge className={STATUS_BADGE[defect.status]}>
                      {statusLabels[defect.status]}
                    </Badge>
                  </span>
                </div>

                <p className="mt-1.5 text-sm font-semibold text-slate-900">{defect.title}</p>
                <p className="mt-0.5 text-[10px] text-slate-500">
                  {defect.reportedByName} · {whenLabel(defect.reportedUtc, false)}
                </p>

                {defect.garageName && (
                  <p className="mt-1 text-[10px] font-medium text-blue-600">
                    → {defect.garageName}
                  </p>
                )}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

function Badge({ className, children }: { className: string; children: React.ReactNode }) {
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase ${className}`}
    >
      {children}
    </span>
  );
}
